import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  GearSix,
  CircleNotch,
  Plus,
  Trash,
  CheckCircle,
  PaintBrush,
  Check,
  DownloadSimple,
  FolderOpen,
  Clock,
  CalendarBlank,
  Export
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { exportAllData } from '../utils/csvExport';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const themeSwatches = [
  { key: 'softGreen', label: 'Soft Green', color: '#16A34A', bg: '#F0FDF4' },
  { key: 'deepBlue', label: 'Deep Blue', color: '#3B82F6', bg: '#0F172A' },
  { key: 'orange', label: 'Orange', color: '#F97316', bg: '#FFF7ED' },
  { key: 'purple', label: 'Purple', color: '#8B5CF6', bg: '#FAF5FF' },
  { key: 'light', label: 'Light', color: '#10B981', bg: '#F9FAFB' },
  { key: 'darkGreen', label: 'Dark Green', color: '#22C55E', bg: '#0A1F14' },
  { key: 'black', label: 'Black', color: '#FFFFFF', bg: '#0A0A0A' },
  { key: 'red', label: 'Red', color: '#EF4444', bg: '#1F0A0A' },
  { key: 'teal', label: 'Teal', color: '#14B8A6', bg: '#0A1F1F' },
  { key: 'grey', label: 'Grey', color: '#A0A0A0', bg: '#141414' },
  { key: 'neon', label: 'Neon', color: '#39FF14', bg: '#0A0F0A' },
  { key: 'gold', label: 'Gold', color: '#EAB308', bg: '#1A170A' },
  { key: 'pink', label: 'Pink', color: '#EC4899', bg: '#1F0A18' },
  { key: 'cyan', label: 'Cyan', color: '#06B6D4', bg: '#0A1A1F' },
];

export default function Settings() {
  const { user, hasPermission } = useAuth();
  const { t } = useLanguage();
  const { theme, setTheme } = useTheme();
  const isAdmin = user?.role === 'admin';

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [staff, setStaff] = useState([]);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'staff' });
  const [creatingUser, setCreatingUser] = useState(false);

  // Export state
  const [exportSchedule, setExportSchedule] = useState({
    enabled: false, frequency: 'daily', time: '21:00', day_of_week: 0, day_of_month: 1
  });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [folderHandle, setFolderHandle] = useState(null);
  const [folderName, setFolderName] = useState('');
  const [lastExportTime, setLastExportTime] = useState(null);
  const exportIntervalRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes] = await Promise.all([
          axios.get(`${API_URL}/api/settings`, { withCredentials: true }),
        ]);
        setSettings(settingsRes.data);
        if (isAdmin) {
          try {
            const staffRes = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
            setStaff(staffRes.data);
          } catch (e) { console.error('Error fetching staff:', e); }
          try {
            const schedRes = await axios.get(`${API_URL}/api/settings/export-schedule`, { withCredentials: true });
            setExportSchedule(schedRes.data);
          } catch (e) { console.error('Error fetching export schedule:', e); }
        }
      } catch (error) { console.error('Error fetching settings:', error); }
      finally { setLoading(false); }
    };
    fetchData();
    // Load last export time from localStorage
    const stored = localStorage.getItem('dm_last_export');
    if (stored) setLastExportTime(stored);
    const savedFolder = localStorage.getItem('dm_export_folder');
    if (savedFolder) setFolderName(savedFolder);
  }, [isAdmin]);

  // Auto-export scheduler
  const runExport = useCallback(async () => {
    try {
      const results = await exportAllData(API_URL);
      if (results.length > 0) {
        const now = new Date().toISOString();
        localStorage.setItem('dm_last_export', now);
        setLastExportTime(now);
        toast.success(t('exportComplete') + ` (${results.length} files)`);
      }
    } catch (err) {
      console.error('Auto export failed:', err);
    }
  }, [t]);

  useEffect(() => {
    if (!exportSchedule.enabled) {
      if (exportIntervalRef.current) clearInterval(exportIntervalRef.current);
      return;
    }
    // Check every 60 seconds if scheduled time matches
    const checkSchedule = () => {
      const now = new Date();
      const [schedH, schedM] = exportSchedule.time.split(':').map(Number);
      if (now.getHours() === schedH && now.getMinutes() === schedM) {
        const lastRun = localStorage.getItem('dm_last_export_run_date');
        const today = now.toISOString().split('T')[0];
        if (lastRun === today) return; // Already ran today

        let shouldRun = false;
        if (exportSchedule.frequency === 'daily') shouldRun = true;
        else if (exportSchedule.frequency === 'weekly' && now.getDay() === ((exportSchedule.day_of_week + 1) % 7)) shouldRun = true;
        else if (exportSchedule.frequency === 'monthly' && now.getDate() === exportSchedule.day_of_month) shouldRun = true;

        if (shouldRun) {
          localStorage.setItem('dm_last_export_run_date', today);
          runExport();
        }
      }
    };
    exportIntervalRef.current = setInterval(checkSchedule, 60000);
    return () => { if (exportIntervalRef.current) clearInterval(exportIntervalRef.current); };
  }, [exportSchedule, runExport]);

  const handleExportNow = async () => {
    setIsExporting(true);
    try {
      const results = await exportAllData(API_URL);
      if (results.length > 0) {
        const now = new Date().toISOString();
        localStorage.setItem('dm_last_export', now);
        setLastExportTime(now);
        toast.success(t('exportComplete') + ` (${results.length} files)`);
      } else {
        toast.info('No data to export');
      }
    } catch (err) {
      toast.error(t('exportFailed'));
    } finally { setIsExporting(false); }
  };

  const handleChooseFolder = async () => {
    // Use native file input to let user pick folder reference
    const input = document.createElement('input');
    input.type = 'file';
    input.webkitdirectory = true;
    input.addEventListener('change', (e) => {
      if (e.target.files && e.target.files.length > 0) {
        const path = e.target.files[0].webkitRelativePath.split('/')[0];
        setFolderName(path);
        localStorage.setItem('dm_export_folder', path);
        toast.success(t('folderSelected') + ': ' + path);
      }
    });
    input.click();
  };

  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    try {
      const res = await axios.put(`${API_URL}/api/settings/export-schedule`, exportSchedule, { withCredentials: true });
      setExportSchedule(res.data);
      toast.success(t('scheduleSaved'));
    } catch (err) { toast.error('Failed to save schedule'); }
    finally { setSavingSchedule(false); }
  };

  const getNextExportText = () => {
    if (!exportSchedule.enabled) return t('never');
    const [h, m] = exportSchedule.time.split(':');
    const now = new Date();
    const next = new Date();
    next.setHours(parseInt(h), parseInt(m), 0, 0);
    if (next <= now) next.setDate(next.getDate() + 1);
    if (exportSchedule.frequency === 'weekly') {
      const targetDay = (exportSchedule.day_of_week + 1) % 7; // JS Sunday=0
      while (next.getDay() !== targetDay) next.setDate(next.getDate() + 1);
    } else if (exportSchedule.frequency === 'monthly') {
      next.setDate(exportSchedule.day_of_month);
      if (next <= now) next.setMonth(next.getMonth() + 1);
    }
    return next.toLocaleDateString() + ' ' + next.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];


  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/settings`, settings, { withCredentials: true });
      toast.success('Settings saved successfully!');
    } catch (error) { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreatingUser(true);
    try {
      await axios.post(`${API_URL}/api/auth/register`, newUser, { withCredentials: true });
      toast.success('Staff user created!');
      setShowUserDialog(false);
      setNewUser({ name: '', email: '', password: '', role: 'staff' });
      const staffRes = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
      setStaff(staffRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally { setCreatingUser(false); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API_URL}/api/users/${userId}`, { withCredentials: true });
      toast.success('User deleted');
      setStaff(staff.filter(u => u.id !== userId));
    } catch (error) { toast.error('Failed to delete user'); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><CircleNotch size={32} className="animate-spin" style={{ color: 'var(--dm-accent)' }} /></div>;
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-slide-up" data-testid="settings-page">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-black/40 mx-auto mb-4 flex items-center justify-center rounded-2xl">
          <GearSix size={32} style={{ color: 'var(--dm-accent)' }} />
        </div>
        <h2 className="text-2xl font-bold text-[var(--text)]" style={{ fontFamily: 'Outfit' }}>{t('adminSettingsTitle')}</h2>
        <p style={{ color: 'var(--dm-text-muted)' }}>{t('configureStore')}</p>
      </div>

      <Tabs defaultValue="general">
        <TabsList className="p-1 w-full grid grid-cols-4 bg-[#1E293B] border border-[#334155] rounded-lg">
          <TabsTrigger value="general" className="data-[state=active]:bg-[#0F172A] data-[state=active]:text-white text-[#94A3B8] rounded-lg text-xs sm:text-sm" data-testid="tab-general">
            {t('general')}
          </TabsTrigger>
          <TabsTrigger value="appearance" className="data-[state=active]:bg-[#0F172A] data-[state=active]:text-white text-[#94A3B8] rounded-lg text-xs sm:text-sm" data-testid="tab-appearance">
            <PaintBrush size={16} className="mr-1 sm:mr-2" />Colors
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="users" className="data-[state=active]:bg-[#0F172A] data-[state=active]:text-white text-[#94A3B8] rounded-lg text-xs sm:text-sm" data-testid="tab-users">
              {t('userAccess')}
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="export" className="data-[state=active]:bg-[#0F172A] data-[state=active]:text-white text-[#94A3B8] rounded-lg text-xs sm:text-sm" data-testid="tab-export">
              <Export size={16} className="mr-1 sm:mr-2" />{t('dataExport')}
            </TabsTrigger>
          )}
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general" className="mt-4">
          <div className="space-y-6 p-6 rounded-xl" style={{ background: 'var(--dm-bg-card)', border: `1px solid var(--dm-border)` }}>
            <div>
              <Label className="text-sm mb-2 block" style={{ color: 'var(--dm-text-muted)' }}>{t('storeName')}</Label>
              <Input value={settings?.store_name || ''} onChange={(e) => setSettings({ ...settings, store_name: e.target.value })}
                data-testid="store-name-input" className="text-[var(--text)] rounded-xl h-12"
                style={{ background: 'var(--dm-input-bg)', borderColor: 'var(--dm-border)' }} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm mb-2 block" style={{ color: 'var(--dm-text-muted)' }}>{t('currency')}</Label>
                <Input value={settings?.currency || 'USD'} onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
                  data-testid="currency-input" className="text-[var(--text)] rounded-xl h-12"
                  style={{ background: 'var(--dm-input-bg)', borderColor: 'var(--dm-border)' }} />
              </div>
              <div>
                <Label className="text-sm mb-2 block" style={{ color: 'var(--dm-text-muted)' }}>{t('defaultTax')}</Label>
                <Input type="number" value={settings?.default_tax || '0'} onChange={(e) => setSettings({ ...settings, default_tax: e.target.value })}
                  data-testid="tax-input" className="text-[var(--text)] rounded-xl h-12"
                  style={{ background: 'var(--dm-input-bg)', borderColor: 'var(--dm-border)' }} />
              </div>
            </div>
            <div>
              <Label className="text-sm mb-2 block" style={{ color: 'var(--dm-text-muted)' }}>{t('invoiceFooter')}</Label>
              <Textarea value={settings?.invoice_footer || ''} onChange={(e) => setSettings({ ...settings, invoice_footer: e.target.value })}
                data-testid="invoice-footer-input" className="text-[var(--text)] rounded-xl resize-none" rows={3}
                style={{ background: 'var(--dm-input-bg)', borderColor: 'var(--dm-border)' }} />
            </div>
            <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-settings-btn"
              className="w-full h-14 bg-black text-white font-bold text-lg rounded-xl hover:bg-black/80 disabled:opacity-50"
              style={{ border: `1px solid var(--dm-border)` }}>
              {saving ? <CircleNotch size={24} className="animate-spin" /> : <><CheckCircle size={24} className="mr-2" />{t('saveSettings')}</>}
            </Button>
          </div>
        </TabsContent>

        {/* Appearance/Colors Tab */}
        <TabsContent value="appearance" className="mt-4">
          <div className="space-y-6 p-6 rounded-xl" style={{ background: 'var(--dm-bg-card)', border: `1px solid var(--dm-border)` }}>
            <div>
              <h3 className="text-lg font-semibold text-[var(--text)] mb-2" style={{ fontFamily: 'Outfit' }}>App Color Theme</h3>
              <p className="text-sm mb-6" style={{ color: 'var(--dm-text-muted)' }}>Choose your preferred color theme for the application</p>

              <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
                {themeSwatches.map((swatch) => (
                  <button
                    key={swatch.key}
                    onClick={() => setTheme(swatch.key)}
                    data-testid={`theme-${swatch.key}`}
                    className={`relative p-4 rounded-xl border-2 transition-all duration-200 hover:scale-105 ${
                      theme === swatch.key ? 'ring-2 ring-offset-2' : ''
                    }`}
                    style={{
                      background: swatch.bg,
                      borderColor: theme === swatch.key ? swatch.color : 'var(--dm-border)',
                      ...(theme === swatch.key ? { '--tw-ring-color': swatch.color, '--tw-ring-offset-color': 'var(--bg)' } : {}),
                    }}
                  >
                    {theme === swatch.key && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center" style={{ background: swatch.color }}>
                        <Check size={14} weight="bold" className="text-black" />
                      </div>
                    )}
                    <div className="flex flex-col items-center gap-3 py-2">
                      <div className="w-12 h-12 rounded-full border-4" style={{ borderColor: swatch.color, background: `${swatch.color}20` }} />
                      <span className="text-sm font-semibold" style={{ color: swatch.bg.startsWith('#0') || swatch.bg.startsWith('#1') ? '#E2E8F0' : '#1F2937' }}>{swatch.label}</span>
                    </div>
                    {/* Preview bar */}
                    <div className="mt-3 flex gap-1.5">
                      <div className="h-2 flex-1 rounded-full" style={{ background: swatch.color }} />
                      <div className="h-2 flex-1 rounded-full" style={{ background: `${swatch.color}60` }} />
                      <div className="h-2 flex-1 rounded-full" style={{ background: `${swatch.color}30` }} />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Users Tab */}
        {isAdmin && (
          <TabsContent value="users" className="mt-4">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-[var(--text)]">{t('userAccessControl')}</h3>
                <Button onClick={() => setShowUserDialog(true)} data-testid="add-staff-btn"
                  className="bg-black text-white font-semibold rounded-xl" style={{ border: `1px solid var(--dm-border)` }}>
                  <Plus size={18} className="mr-2" />{t('addStaffUser')}
                </Button>
              </div>

              <div className="space-y-3">
                {staff.length === 0 ? (
                  <p className="text-center py-8" style={{ color: 'var(--dm-text-muted)' }}>{t('noStaffUsers')}</p>
                ) : (
                  staff.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-4 rounded-xl"
                      style={{ background: 'var(--dm-bg-card)', border: `1px solid var(--dm-border)` }}
                      data-testid={`user-row-${member.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-black flex items-center justify-center font-bold uppercase rounded-full"
                          style={{ color: 'var(--dm-accent)' }}>
                          {member.name?.charAt(0) || 'U'}
                        </div>
                        <div>
                          <p className="text-[var(--text)] font-medium">{member.name}</p>
                          <p className="text-xs" style={{ color: 'var(--dm-text-muted)' }}>{member.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${member.role === 'admin' ? 'bg-[var(--primary)]/20 text-[var(--primary)]' : 'bg-[#3B82F6]/20 text-[#3B82F6]'}`}>
                          {member.role}
                        </span>
                        {member.role !== 'admin' && (
                          <button onClick={() => handleDeleteUser(member.id)} className="text-[var(--dm-text-muted)] hover:text-[#EF4444] transition-colors" data-testid={`delete-user-${member.id}`}>
                            <Trash size={18} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>
        )}

        {/* Data Export Tab */}
        {isAdmin && (
          <TabsContent value="export" className="mt-4">
            <div className="space-y-6">
              {/* Export Now Section */}
              <div className="p-6 rounded-xl" style={{ background: 'var(--dm-bg-card)', border: '1px solid var(--dm-border)' }}>
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-black/40 flex items-center justify-center shrink-0">
                    <DownloadSimple size={24} style={{ color: 'var(--dm-accent)' }} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[var(--text)]" style={{ fontFamily: 'Outfit' }}>{t('exportAllData')}</h3>
                    <p className="text-sm" style={{ color: 'var(--dm-text-muted)' }}>{t('exportAllDesc')}</p>
                  </div>
                </div>

                {/* Save Location */}
                <div className="mb-4 p-4 rounded-lg" style={{ background: 'var(--dm-input-bg)', border: '1px solid var(--dm-border)' }}>
                  <Label className="text-sm mb-2 block font-semibold" style={{ color: 'var(--dm-text-muted)' }}>
                    <FolderOpen size={16} className="inline mr-2" />{t('saveFolder')}
                  </Label>
                  <p className="text-xs mb-3" style={{ color: 'var(--dm-text-muted)' }}>{t('saveFolderDesc')}</p>
                  <div className="flex items-center gap-3">
                    <Button onClick={handleChooseFolder} variant="outline" data-testid="choose-folder-btn"
                      className="rounded-xl text-sm" style={{ borderColor: 'var(--dm-border)', color: 'var(--text)' }}>
                      <FolderOpen size={16} className="mr-2" />{t('chooseFolder')}
                    </Button>
                    <span className="text-sm truncate" style={{ color: folderName ? 'var(--dm-accent)' : 'var(--dm-text-muted)' }}>
                      {folderName ? `${folderName}/` : t('noFolderSelected')}
                    </span>
                  </div>
                </div>

                {/* Last Export Info */}
                {lastExportTime && (
                  <p className="text-xs mb-4" style={{ color: 'var(--dm-text-muted)' }}>
                    {t('lastExport')}: {new Date(lastExportTime).toLocaleString()}
                  </p>
                )}

                <Button onClick={handleExportNow} disabled={isExporting} data-testid="export-now-btn"
                  className="w-full h-14 bg-black text-white font-bold text-lg rounded-xl hover:bg-black/80 disabled:opacity-50"
                  style={{ border: '1px solid var(--dm-border)' }}>
                  {isExporting ? (
                    <><CircleNotch size={24} className="animate-spin mr-2" />{t('exporting')}</>
                  ) : (
                    <><DownloadSimple size={24} className="mr-2" />{t('exportNow')}</>
                  )}
                </Button>
              </div>

              {/* Auto Export Schedule */}
              <div className="p-6 rounded-xl" style={{ background: 'var(--dm-bg-card)', border: '1px solid var(--dm-border)' }}>
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-black/40 flex items-center justify-center shrink-0">
                    <Clock size={24} style={{ color: 'var(--dm-accent)' }} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[var(--text)]" style={{ fontFamily: 'Outfit' }}>{t('autoExport')}</h3>
                    <p className="text-sm" style={{ color: 'var(--dm-text-muted)' }}>{t('autoExportDesc')}</p>
                  </div>
                </div>

                {/* Enable Toggle */}
                <div className="flex items-center justify-between mb-6 p-4 rounded-lg" style={{ background: 'var(--dm-input-bg)', border: '1px solid var(--dm-border)' }}>
                  <span className="text-sm font-semibold text-[var(--text)]">{t('scheduleEnabled')}</span>
                  <button onClick={() => setExportSchedule(s => ({ ...s, enabled: !s.enabled }))}
                    data-testid="schedule-toggle"
                    className={`relative w-14 h-7 rounded-full transition-colors duration-200 ${exportSchedule.enabled ? '' : 'bg-gray-600'}`}
                    style={exportSchedule.enabled ? { background: 'var(--dm-accent)' } : {}}>
                    <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform duration-200 ${exportSchedule.enabled ? 'translate-x-7' : 'translate-x-0.5'}`} />
                  </button>
                </div>

                {exportSchedule.enabled && (
                  <div className="space-y-4">
                    {/* Frequency */}
                    <div>
                      <Label className="text-sm mb-2 block" style={{ color: 'var(--dm-text-muted)' }}>{t('frequency')}</Label>
                      <Select value={exportSchedule.frequency} onValueChange={(v) => setExportSchedule(s => ({ ...s, frequency: v }))}>
                        <SelectTrigger className="text-[var(--text)] rounded-xl h-12" data-testid="schedule-frequency"
                          style={{ background: 'var(--dm-input-bg)', borderColor: 'var(--dm-border)' }}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent style={{ background: '#1E293B', borderColor: 'var(--dm-border)' }}>
                          <SelectItem value="daily" className="text-white focus:bg-[var(--dm-accent)] focus:text-white">{t('daily')}</SelectItem>
                          <SelectItem value="weekly" className="text-white focus:bg-[var(--dm-accent)] focus:text-white">{t('weekly')}</SelectItem>
                          <SelectItem value="monthly" className="text-white focus:bg-[var(--dm-accent)] focus:text-white">{t('monthly')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Time */}
                    <div>
                      <Label className="text-sm mb-2 block" style={{ color: 'var(--dm-text-muted)' }}>
                        <Clock size={14} className="inline mr-1" />{t('exportTime')}
                      </Label>
                      <Input type="time" value={exportSchedule.time}
                        onChange={(e) => setExportSchedule(s => ({ ...s, time: e.target.value }))}
                        data-testid="schedule-time"
                        className="text-[var(--text)] rounded-xl h-12"
                        style={{ background: 'var(--dm-input-bg)', borderColor: 'var(--dm-border)' }} />
                    </div>

                    {/* Day of Week (for weekly) */}
                    {exportSchedule.frequency === 'weekly' && (
                      <div>
                        <Label className="text-sm mb-2 block" style={{ color: 'var(--dm-text-muted)' }}>
                          <CalendarBlank size={14} className="inline mr-1" />{t('dayOfWeek')}
                        </Label>
                        <Select value={String(exportSchedule.day_of_week)} onValueChange={(v) => setExportSchedule(s => ({ ...s, day_of_week: parseInt(v) }))}>
                          <SelectTrigger className="text-[var(--text)] rounded-xl h-12" data-testid="schedule-day-of-week"
                            style={{ background: 'var(--dm-input-bg)', borderColor: 'var(--dm-border)' }}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent style={{ background: '#1E293B', borderColor: 'var(--dm-border)' }}>
                            {dayNames.map((d, i) => <SelectItem key={i} value={String(i)} className="text-white focus:bg-[var(--dm-accent)] focus:text-white">{d}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Day of Month (for monthly) */}
                    {exportSchedule.frequency === 'monthly' && (
                      <div>
                        <Label className="text-sm mb-2 block" style={{ color: 'var(--dm-text-muted)' }}>
                          <CalendarBlank size={14} className="inline mr-1" />{t('dayOfMonth')}
                        </Label>
                        <Select value={String(exportSchedule.day_of_month)} onValueChange={(v) => setExportSchedule(s => ({ ...s, day_of_month: parseInt(v) }))}>
                          <SelectTrigger className="text-[var(--text)] rounded-xl h-12" data-testid="schedule-day-of-month"
                            style={{ background: 'var(--dm-input-bg)', borderColor: 'var(--dm-border)' }}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent style={{ background: '#1E293B', borderColor: 'var(--dm-border)' }}>
                            {Array.from({ length: 28 }, (_, i) => (
                              <SelectItem key={i + 1} value={String(i + 1)} className="text-white focus:bg-[var(--dm-accent)] focus:text-white">{i + 1}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Next Export Info */}
                    <div className="p-4 rounded-lg" style={{ background: 'var(--dm-input-bg)', border: '1px solid var(--dm-border)' }}>
                      <div className="flex items-center gap-2">
                        <CalendarBlank size={16} style={{ color: 'var(--dm-accent)' }} />
                        <span className="text-sm font-medium text-[var(--text)]">{t('nextExport')}:</span>
                        <span className="text-sm" style={{ color: 'var(--dm-accent)' }}>{getNextExportText()}</span>
                      </div>
                      <p className="text-xs mt-2" style={{ color: 'var(--dm-text-muted)' }}>
                        * Auto-export runs when the app is open at the scheduled time
                      </p>
                    </div>

                    {/* Save Schedule Button */}
                    <Button onClick={handleSaveSchedule} disabled={savingSchedule} data-testid="save-schedule-btn"
                      className="w-full h-14 bg-black text-white font-bold text-lg rounded-xl hover:bg-black/80 disabled:opacity-50"
                      style={{ border: '1px solid var(--dm-border)' }}>
                      {savingSchedule ? (
                        <CircleNotch size={24} className="animate-spin" />
                      ) : (
                        <><CheckCircle size={24} className="mr-2" />{t('saveSchedule')}</>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Create User Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="text-[var(--text)] rounded-xl" style={{ background: 'var(--dm-bg-card)', borderColor: 'var(--dm-border)' }}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Outfit' }}>{t('createUser')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-5 mt-4">
            <div>
              <Label className="text-sm mb-2 block" style={{ color: 'var(--dm-text-muted)' }}>{t('fullName')} *</Label>
              <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                required data-testid="new-user-name" className="text-[var(--text)] rounded-xl h-12"
                style={{ background: 'var(--dm-input-bg)', borderColor: 'var(--dm-border)' }} />
            </div>
            <div>
              <Label className="text-sm mb-2 block" style={{ color: 'var(--dm-text-muted)' }}>{t('email')} *</Label>
              <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                required data-testid="new-user-email" className="text-[var(--text)] rounded-xl h-12"
                style={{ background: 'var(--dm-input-bg)', borderColor: 'var(--dm-border)' }} />
            </div>
            <div>
              <Label className="text-sm mb-2 block" style={{ color: 'var(--dm-text-muted)' }}>{t('password')} *</Label>
              <Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                required data-testid="new-user-password" className="text-[var(--text)] rounded-xl h-12"
                style={{ background: 'var(--dm-input-bg)', borderColor: 'var(--dm-border)' }} />
            </div>
            <div>
              <Label className="text-sm mb-2 block" style={{ color: 'var(--dm-text-muted)' }}>Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger className="text-[var(--text)] rounded-xl h-12" data-testid="new-user-role"
                  style={{ background: 'var(--dm-input-bg)', borderColor: 'var(--dm-border)' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ background: '#1E293B', borderColor: 'var(--dm-border)' }}>
                  <SelectItem value="staff" className="text-white focus:bg-[var(--dm-accent)] focus:text-white">Staff</SelectItem>
                  <SelectItem value="admin" className="text-white focus:bg-[var(--dm-accent)] focus:text-white">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-4" style={{ borderTop: `1px solid var(--dm-border)` }}>
              <Button type="button" variant="outline" onClick={() => setShowUserDialog(false)}
                className="rounded-xl" style={{ borderColor: 'var(--dm-border)', color: 'var(--dm-text-muted)' }}>{t('cancel')}</Button>
              <Button type="submit" disabled={creatingUser} data-testid="create-user-submit"
                className="bg-black text-white font-semibold rounded-xl" style={{ border: `1px solid var(--dm-border)` }}>
                {creatingUser ? <CircleNotch size={20} className="animate-spin" /> : t('createUser')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
